import { DEBUG } from "../constants";
import { TokenAmount } from "../types";
import { tokenAmountToUiNumber } from "./bigint-math";

function isTokenAmount(value: unknown): value is TokenAmount {
    return (
        typeof value === "object" &&
        value !== null &&
        typeof (value as TokenAmount).amount === "bigint" &&
        typeof (value as TokenAmount).decimals === "number"
    );
}

/**
 * `JSON.stringify` replacer that makes bigint-bearing domain objects serializable.
 * - `TokenAmount` → labeled object `{ ui, raw, decimals }` (raw kept lossless as a string)
 * - bare `bigint` → its string form
 */
export function bigintReplacer(_key: string, value: unknown): unknown {
    if (isTokenAmount(value)) {
        return { ui: tokenAmountToUiNumber(value), raw: value.amount.toString(), decimals: value.decimals };
    }
    if (typeof value === "bigint") return value.toString();
    return value;
}

/**
 * Dev-only console logger for domain objects full of bigint. Prints a clean,
 * expandable object (no `n` suffixes) with any `TokenAmount` rendered readably.
 * No-op outside local dev.
 */
export function logDomain(label: string, obj: unknown): void {
    if (!DEBUG) return;
    // eslint-disable-next-line no-console
    console.log(label, JSON.parse(JSON.stringify(obj, bigintReplacer)));
}

/**
 * Dev-only global shim so plain `JSON.stringify(bigint)` yields a string instead
 * of throwing `TypeError: Do not know how to serialize a BigInt`. Call once at
 * app startup. No-op outside local dev. `TokenAmount` rendering still requires
 * `logDomain` / `bigintReplacer`; this only stops the throw.
 */
export function installBigIntDebug(): void {
    if (!DEBUG || typeof BigInt === "undefined") return;
    const proto = BigInt.prototype as unknown as { toJSON?: () => string };
    if (proto.toJSON) return;
    proto.toJSON = function (this: bigint) {
        return this.toString();
    };
}
